/**
 * GrowthCommandCenter — Meta Ads ROI Executive Dashboard
 * Fetches from /marketing/growth/meta-ads-executive?period={period}
 * Purpose: Executive decision-making in 5 seconds
 * Answers: "Am I making money? Which campaign to scale/kill?"
 */

import React, { useState, useEffect } from "react";
import { AlertCircle, Loader, TrendingUp, TrendingDown } from "lucide-react";
import GrowthCommandCenterCampaignsAlertsSection from "./GrowthCommandCenterCampaignsAlertsSection";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

export default function GrowthCommandCenter({ authAxios, showHeader = true, showPeriodSelector = true }) {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetaAdsROI = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authAxios.get(`/marketing/growth/meta-ads-executive?period=${period}`);
        setData(response.data);
      } catch (err) {
        console.error("[GrowthCommandCenter] Error fetching Meta Ads ROI:", err);
        setError(err.response?.data?.detail || "Erro ao carregar dados de Meta Ads");
      } finally {
        setLoading(false);
      }
    };
    fetchMetaAdsROI();
  }, [period, authAxios]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-slate-200 to-slate-100 rounded-lg h-8 w-64 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-lg h-32 animate-pulse" />
          ))}
        </div>
        <div className="flex items-center justify-center h-32">
          <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-red-900">Erro ao carregar Meta Ads ROI</h3>
          <p className="text-red-700 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // No data
  if (!data) {
    return (
      <div className="bg-slate-50 rounded-lg p-8 text-center border border-slate-200">
        <p className="text-muted-foreground">Nenhuma campanha Meta Ads configurada</p>
        <p className="text-xs text-muted-foreground mt-2">Configure Meta Ads para ver ROI e recomendações</p>
      </div>
    );
  }

  const investimento = data.investimento || {};
  const clientes = data.clientes || {};
  const resultado = data.resultado || {};
  const acoes = data.acoes || [];

  // Decision logic: 3-level ROAS scoring (passed to sub-component)
  // ROAS < 1,5 → CRÍTICO (vermelho)
  // ROAS 1,5 até < 3 → ATENÇÃO (amarelo)
  // ROAS ≥ 3 → SAUDÁVEL (verde)
  const roas = investimento.roas || 0;
  let decisionColor, decisionBadgeColor, decisionLabel, decisionTrend;

  if (roas < 1.5) {
    decisionColor = "border-red-200 bg-red-50";
    decisionBadgeColor = "bg-red-100 text-red-800";
    decisionLabel = roas < 1 ? "✗ Crítico" : "⚠ Atenção";
    decisionTrend = "down";
  } else if (roas < 3) {
    decisionColor = "border-yellow-200 bg-yellow-50";
    decisionBadgeColor = "bg-yellow-100 text-yellow-800";
    decisionLabel = "⚠ Otimizar";
    decisionTrend = "flat";
  } else {
    decisionColor = "border-green-200 bg-green-50";
    decisionBadgeColor = "bg-green-100 text-green-800";
    decisionLabel = "✓ Saudável";
    decisionTrend = "up";
  }


  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      {showHeader && (
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight">Growth Command Center</h1>
          <p className="text-muted-foreground mt-1">Meta Ads — Dashboard Executivo em 5 segundos</p>
        </div>
      )}

      {/* Period Selector */}
      {showPeriodSelector && (
        <div className="flex gap-2 bg-secondary p-1 rounded-lg w-fit">
          {["today", "7d", "30d", "90d"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                period === p
                  ? "bg-white text-black shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p === "today" ? "Hoje" : p === "7d" ? "7 dias" : p === "30d" ? "30 dias" : "90 dias"}
            </button>
          ))}
        </div>
      )}

      {/* LINHA 1 — Decision Box (Profitability at a glance) */}
      <div className={`border-2 rounded-lg p-6 ${decisionColor}`}>
        <div className="flex items-center gap-2 mb-3">
          {decisionTrend === "up" ? (
            <TrendingUp className="w-5 h-5 text-green-600" />
          ) : decisionTrend === "down" ? (
            <TrendingDown className="w-5 h-5 text-red-600" />
          ) : (
            <TrendingUp className="w-5 h-5 text-yellow-600" />
          )}
          <span className={`text-xs font-bold uppercase px-2 py-1 rounded-full ${decisionBadgeColor}`}>
            {decisionLabel}
          </span>
        </div>
        <p className={`text-sm font-medium mb-3 ${
          decisionTrend === "up" ? "text-green-700" : decisionTrend === "down" ? "text-red-700" : "text-yellow-700"
        }`}>
          {roas >= 1
            ? `Ganho de ${formatCurrency(investimento.roas * investimento.investido - investimento.investido)}`
            : `Perda de ${formatCurrency(investimento.investido - investimento.receita)}`}
        </p>
        <p className={`text-xs ${
          decisionTrend === "up" ? "text-green-600" : decisionTrend === "down" ? "text-red-600" : "text-yellow-600"
        }`}>
          ROAS: {investimento.roas?.toFixed(2)}x | CAC: {formatCurrency(clientes.cac)} | Ticket: {formatCurrency(clientes.ticket)}
        </p>
      </div>

      {/* LINHA 1 — Decision Cards (4 Cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card: INVESTIMENTO */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-2">Investimento</p>
          <p className="text-3xl font-bold text-slate-900 mb-1">{formatCurrency(investimento.investido)}</p>
          <p className="text-xs text-muted-foreground">Gasto em anúncios</p>
        </div>

        {/* Card: RECEITA ATRIBUÍDA */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-2">Receita Atribuída</p>
          <p className="text-3xl font-bold text-slate-900 mb-1">{formatCurrency(investimento.receita)}</p>
          <p className="text-xs text-muted-foreground">Vendas via Meta Ads</p>
        </div>

        {/* Card: ROAS */}
        <div className={`border rounded-lg p-5 shadow-sm hover:shadow-md transition ${
          decisionTrend === "up"
            ? "bg-green-50 border-green-200"
            : decisionTrend === "down"
            ? "bg-red-50 border-red-200"
            : "bg-yellow-50 border-yellow-200"
        }`}>
          <p className="text-xs font-bold uppercase text-muted-foreground mb-2">ROAS</p>
          <p className={`text-3xl font-bold mb-1 ${
            decisionTrend === "up"
              ? "text-green-700"
              : decisionTrend === "down"
              ? "text-red-700"
              : "text-yellow-700"
          }`}>
            {investimento.roas?.toFixed(2)}x
          </p>
          <p className={`text-xs ${
            decisionTrend === "up"
              ? "text-green-600"
              : decisionTrend === "down"
              ? "text-red-600"
              : "text-yellow-600"
          }`}>
            Por 1€ gasto
          </p>
        </div>

        {/* Card: CAC */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-2">CAC</p>
          <p className="text-3xl font-bold text-slate-900 mb-1">{formatCurrency(clientes.cac)}</p>
          <p className="text-xs text-muted-foreground">Custo por cliente</p>
        </div>
      </div>

      {/* LINHA 2 — Performance Grid (2x2) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card: CLIENTES NOVOS */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-3">Clientes Novos</p>
          <p className="text-4xl font-bold text-slate-900 mb-2">{clientes.novos || 0}</p>
          <p className="text-sm text-muted-foreground">Primeira compra via Meta Ads</p>
        </div>

        {/* Card: TICKET MÉDIO */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-3">Ticket Médio</p>
          <p className="text-4xl font-bold text-slate-900 mb-2">{formatCurrency(clientes.ticket)}</p>
          <p className="text-sm text-muted-foreground">Valor médio por pedido</p>
        </div>

        {/* Card: LTV */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-3">LTV</p>
          <p className="text-4xl font-bold text-slate-900 mb-2">{formatCurrency(clientes.ltv)}</p>
          <p className="text-sm text-muted-foreground">Valor total esperado por cliente</p>
        </div>

        {/* Card: LUCRO ESTIMADO */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-xs font-bold uppercase text-muted-foreground mb-3">Lucro Estimado</p>
          <p className="text-4xl font-bold text-slate-900 mb-2">{formatCurrency(resultado.lucro_estimado)}</p>
          <p className="text-sm text-muted-foreground">Com margem padrão</p>
        </div>
      </div>

      {/* LINHA 3-4 — Campanhas + Alertas Section */}
      <GrowthCommandCenterCampaignsAlertsSection acoes={acoes} />
    </div>
  );
}
